import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import { Box, Stack, Tooltip, Typography, useTheme } from "@mui/material";
import { type ReactNode, useEffect, useState } from "react";
import { getUserBanners, type UserBanners } from "../../api/userprofile";
import { formatRelativeTimeShort } from "../../utils/date-formatting";
import { EmailSyncFailureAlert } from "../EmailSyncFailureBanner";
import { PluginHealthAlert, pluginHealthSeverity } from "../plugin-health-banner";

export interface ManagedUser {
	id: number;
	username: string;
	first_name: string;
	last_name: string;
	last_todo: string | null;
	is_internal: boolean;
}

type Severity = "error" | "warning" | "info";

// Banners change over hours; the same cadence the banners themselves refresh at.
const REFRESH_INTERVAL_MS = 5 * 60_000;

/** Every active user's own banners, keyed by user id; polled only for staff. */
export const useUserBanners = (enabled: boolean): Record<number, UserBanners> => {
	const [banners, setBanners] = useState<Record<number, UserBanners>>({});

	useEffect(() => {
		if (!enabled) return;
		let mounted = true;
		const load = () => {
			getUserBanners()
				.then((b) => {
					if (mounted) setBanners(b);
				})
				.catch(() => {});
		};
		load();
		const id = window.setInterval(load, REFRESH_INTERVAL_MS);
		return () => {
			mounted = false;
			window.clearInterval(id);
		};
	}, [enabled]);

	return banners;
};

interface ShownBanner {
	severity: Severity;
	alert: ReactNode;
}

/** The banners this user sees, most severe first, as the banners render them. */
const shownBanners = (banners: UserBanners | undefined): ShownBanner[] => {
	if (!banners) return [];
	const shown: ShownBanner[] = [];
	if (banners.email_sync_error_is_persistent) {
		shown.push({
			severity: "error",
			alert: <EmailSyncFailureAlert emailMethod={banners.email_method} />,
		});
	}
	const health = banners.plugin_health;
	if (health.problem) {
		shown.push({
			severity: pluginHealthSeverity(health),
			alert: <PluginHealthAlert health={health} />,
		});
	}
	return shown;
};

const displayName = (user: ManagedUser): string =>
	user.first_name || user.last_name
		? `${user.first_name} ${user.last_name}`.trim()
		: user.username;

const ActiveUserRow = ({
	user,
	banners,
}: {
	user: ManagedUser;
	banners: UserBanners | undefined;
}) => {
	const theme = useTheme();
	const shown = shownBanners(banners);
	const row = (
		<Box
			sx={{
				fontSize: "0.8rem",
				color: shown.length
					? // The list sits in a tooltip: white in light mode, dark grey in dark.
						theme.palette[shown[0].severity][
							theme.palette.mode === "dark" ? "light" : "main"
						]
					: "inherit",
				fontWeight: shown.length ? 600 : undefined,
				cursor: shown.length ? "help" : undefined,
				display: "flex",
				alignItems: "center",
				gap: 0.5,
			}}
			data-component="active-user-row"
		>
			{shown.length > 0 && (
				<FiberManualRecordIcon
					sx={{ fontSize: "0.6rem" }}
					data-component="active-user-banner-dot"
				/>
			)}
			{displayName(user)} - {formatRelativeTimeShort(user.last_todo)}
		</Box>
	);
	if (!shown.length) return row;

	return (
		<Tooltip
			placement="left"
			arrow={false}
			disableInteractive
			title={
				<Stack spacing={0.5} data-component="active-user-banners">
					{shown.map((b) => (
						<Box key={b.severity} data-component="active-user-banner">
							{b.alert}
						</Box>
					))}
				</Stack>
			}
			slotProps={{
				tooltip: { sx: { bgcolor: "transparent", p: 0, maxWidth: 480 } },
			}}
		>
			{row}
		</Tooltip>
	);
};

const UserGroup = ({
	label,
	users,
	banners,
	last,
}: {
	label: string;
	users: ManagedUser[];
	banners: Record<number, UserBanners>;
	last: boolean;
}) => (
	<>
		<Typography
			variant="subtitle2"
			sx={{ mb: 1, fontWeight: 600 }}
			data-component="active-users-group-label"
		>
			{label}
		</Typography>
		{users.length > 0 ? (
			<Box
				sx={{
					display: "flex",
					flexDirection: "column",
					gap: 0.5,
					mb: last ? 0 : 1.5,
				}}
				data-component="active-users-group"
			>
				{users.map((user) => (
					<ActiveUserRow
						key={user.id}
						user={user}
						banners={banners[user.id]}
					/>
				))}
			</Box>
		) : (
			<Box
				sx={{ fontSize: "0.8rem", mb: last ? 0 : 1.5 }}
				data-component="active-users-empty"
			>
				No active users
			</Box>
		)}
	</>
);

/** The navbar's active-users list. A user whose own screens carry a banner is
 * coloured by its severity; hovering the name shows that banner. */
export const ActiveUsersList = ({
	users1h,
	users72h,
	banners,
}: {
	users1h: ManagedUser[];
	users72h: ManagedUser[];
	banners: Record<number, UserBanners>;
}) => (
	<Box
		sx={{ fontSize: "0.875rem", maxHeight: "300px", overflowY: "auto" }}
		data-component="active-users-list"
	>
		<UserGroup
			label="Active last 1h:"
			users={users1h}
			banners={banners}
			last={false}
		/>
		<UserGroup label="Active last 72h:" users={users72h} banners={banners} last />
	</Box>
);
